<template>
  <!-- A release note's motion: loops of the real app (frames from the app's own visual checks, see
       docs/release-notes.md) inside the same iPad and iPhone hardware as the hero's MobileAppWindow
       (11-inch iPad landscape, 6.3-inch iPhone; bezel, rim and island geometry copied from it), or in the
       story app window for a desktop screen. The phone runs at the app's 125% text. Each device carries a
       light and a dark loop and shows the one that matches the site theme. Loops load and play only on
       screen, and never under reduced motion, where the posters (their last frame) stay. Device frames
       are drawn: rounded corners and the island are the hardware, not site UI. -->
  <figure class="rnd" :class="[`rnd--${motion.layout}`, { 'is-in': shown }]">
    <div ref="stage" class="rnd-stage">
      <div v-if="motion.layout === 'tablet-phone'" class="rnd-ipad">
        <div class="rnd-ipad-screen">
          <div class="rnd-status rnd-status--ipad" aria-hidden="true"><b>9:41</b><span>Thu Oct 29</span><i class="rnd-grow" /><span class="rnd-batt"><i /></span></div>
          <video
            v-for="tone in tones"
            :key="tone"
            :ref="register"
            :class="`rnd-v rnd-v--${tone}`"
            :poster="motion.large[tone].poster"
            :width="motion.large.width"
            :height="motion.large.height"
            :aria-label="tone === 'light' ? motion.alt : undefined"
            :aria-hidden="tone === 'dark' ? 'true' : undefined"
            muted loop playsinline preload="none"
          >
            <source :src="motion.large[tone].webm" type="video/webm" />
            <source :src="motion.large[tone].mp4" type="video/mp4" />
          </video>
        </div>
      </div>

      <div v-else class="rnd-desk">
        <StoryAppWindow :title="motion.large.title || 'Workspace369'" :sub="motion.large.sub || ''">
          <video
            v-for="tone in tones"
            :key="tone"
            :ref="register"
            :class="`rnd-v rnd-v--${tone}`"
            :poster="motion.large[tone].poster"
            :width="motion.large.width"
            :height="motion.large.height"
            :aria-label="tone === 'light' ? motion.alt : undefined"
            :aria-hidden="tone === 'dark' ? 'true' : undefined"
            muted loop playsinline preload="none"
          >
            <source :src="motion.large[tone].webm" type="video/webm" />
            <source :src="motion.large[tone].mp4" type="video/mp4" />
          </video>
        </StoryAppWindow>
      </div>

      <div class="rnd-phone">
        <div class="rnd-phone-screen">
          <div class="rnd-status rnd-status--phone" aria-hidden="true"><b>9:41</b><i class="rnd-island" /><span class="rnd-batt"><i /></span></div>
          <video
            v-for="tone in tones"
            :key="tone"
            :ref="register"
            :class="`rnd-v rnd-v--${tone}`"
            :poster="motion.phone[tone].poster"
            :width="motion.phone.width"
            :height="motion.phone.height"
            aria-hidden="true"
            muted loop playsinline preload="none"
          >
            <source :src="motion.phone[tone].webm" type="video/webm" />
            <source :src="motion.phone[tone].mp4" type="video/mp4" />
          </video>
        </div>
      </div>
    </div>
    <figcaption>{{ motion.caption }}</figcaption>
  </figure>
</template>

<script setup>
const props = defineProps({
  motion: { type: Object, required: true },
});

const tones = ['light', 'dark'];
const stage = ref(null);
const shown = ref(false);
const videos = new Set();
const register = (el) => { if (el) videos.add(el); };

// Only the loop matching the theme is laid out; a hidden one never plays.
const visible = (el) => el.offsetParent !== null || el.getClientRects().length > 0;

let seen;
let themeWatch;
let motionQuery;
let sync;
onMounted(() => {
  motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let onScreen = false;
  sync = () => {
    for (const el of videos) {
      if (onScreen && !motionQuery.matches && visible(el)) el.play().catch(() => {});
      else el.pause();
    }
  };
  motionQuery.addEventListener('change', sync);
  seen = new IntersectionObserver(([entry]) => {
    onScreen = entry.isIntersecting;
    if (onScreen) shown.value = true;
    sync();
  }, { threshold: 0.3 });
  seen.observe(stage.value);
  // The site's theme switch flips .dark on <html>: swap which loop runs.
  themeWatch = new MutationObserver(sync);
  themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
});
onBeforeUnmount(() => {
  motionQuery?.removeEventListener('change', sync);
  seen?.disconnect();
  themeWatch?.disconnect();
});
</script>

<style scoped>
.rnd {
  --bezel: #0d0e10;
  --rim: #3a3d42;
  margin: 2rem 0 0;
}
.dark .rnd { --bezel: #050506; --rim: #6b7078; }
.rnd-stage {
  position: relative;
  container: rnd / inline-size;
  isolation: isolate;
}
/* A soft brand glow behind the hardware. */
.rnd-stage::before {
  content: "";
  position: absolute;
  z-index: -1;
  inset: 8% 4% 0;
  border-radius: 50%;
  background: radial-gradient(closest-side, rgb(27 167 206 / 0.16), transparent);
  filter: blur(20px);
}
.dark .rnd-stage::before { background: radial-gradient(closest-side, rgb(27 167 206 / 0.22), transparent); }

.rnd--tablet-phone .rnd-stage { aspect-ratio: 1248 / 820; }
.rnd--desktop-phone .rnd-stage { aspect-ratio: 1248 / 820; }

.rnd-v { display: block; width: 100%; height: 100%; object-fit: cover; background: transparent; }
.rnd-v--dark { display: none; }
.dark .rnd-v--light { display: none; }
.dark .rnd-v--dark { display: block; }

/* iPad (11-inch, landscape: 1194 × 834 pt), as MobileAppWindow draws it. */
.rnd-ipad {
  position: absolute; top: 2%; left: 0; width: 76%;
  aspect-ratio: 1242 / 882;
  border-radius: 3.4% / 4.8%;
  background: var(--bezel);
  box-shadow: inset 0 0 0 1.5px var(--rim), 0 40px 80px -40px rgb(15 23 42 / 0.45), 0 16px 32px -20px rgb(15 23 42 / 0.2);
}
/* The screen sits inside a 24pt bezel (1194 × 834 in 1242 × 882), as insets of the device itself:
   percentage padding would resolve against the whole stage. */
.rnd-ipad-screen { position: absolute; inset: 2.72% 1.93%; overflow: hidden; border-radius: 1.6% / 2.3%; background: #fafafa; }
.dark .rnd-ipad-screen { background: #1c1c1c; }

/* The desktop: the story app window around a wide app screen. */
.rnd-desk { position: absolute; top: 14%; left: 0; width: 71%; }
.rnd-desk :deep(.saw) { height: auto; }
.rnd-desk :deep(.saw-body) { padding: 0; }
.rnd-desk .rnd-v { height: auto; object-fit: contain; }

/* iPhone (6.3-inch: 402 × 874 pt), as MobileAppWindow draws it. */
.rnd-phone {
  position: absolute; top: 9%; right: 0; width: 27%;
  aspect-ratio: 432 / 904;
  border-radius: 15.5% / 7.4%;
  background: var(--bezel);
  box-shadow: inset 0 0 0 1.5px var(--rim), 0 40px 70px -30px rgb(15 23 42 / 0.55), 0 14px 28px -16px rgb(15 23 42 / 0.3);
}
.dark .rnd-phone, .dark .rnd-ipad { box-shadow: inset 0 0 0 1.5px var(--rim), 0 0 0 1px rgb(148 163 184 / 0.22), 0 40px 80px -30px rgb(0 0 0 / 0.8); }
/* A 15pt bezel: 402 × 874 in 432 × 904. */
.rnd-phone-screen { position: absolute; inset: 1.66% 3.47%; overflow: hidden; border-radius: 12.5% / 5.8%; background: #fafafa; container-type: inline-size; }
.dark .rnd-phone-screen { background: #1c1c1c; }

/* Status bars sit over the app's own safe-area inset, as the hardware's do. */
.rnd-status { position: absolute; z-index: 2; top: 0; right: 0; left: 0; display: flex; align-items: center; color: #0a0a0a; font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif; font-variant-numeric: tabular-nums; }
.dark .rnd-status { color: #fafafa; }
.rnd-status--ipad { gap: 8px; height: 2.9%; min-height: 12px; padding: 0 1.6%; font-size: clamp(7px, 1cqi, 12px); }
.rnd-status--ipad span:not(.rnd-batt) { opacity: 0.7; }
.rnd-status--phone { --u: calc(100cqi / 402); justify-content: space-between; height: calc(var(--u) * 54); padding: 0 calc(var(--u) * 34) 0 calc(var(--u) * 46); font-size: calc(var(--u) * 16); }
.rnd-grow { flex: 1; }
.rnd-batt { position: relative; display: inline-block; width: 22px; height: 11px; border: 1px solid currentColor; border-radius: 3px; opacity: 0.8; }
.rnd-status--phone .rnd-batt { width: calc(var(--u) * 26); height: calc(var(--u) * 12); border-radius: calc(var(--u) * 4); }
.rnd-status--ipad .rnd-batt { width: 1.8cqi; max-width: 22px; height: 0.9cqi; max-height: 11px; }
.rnd-batt i { position: absolute; inset: 1.5px 5px 1.5px 1.5px; border-radius: 1px; background: currentColor; }
.rnd-island { position: absolute; top: calc(var(--u) * 11); left: 50%; width: calc(var(--u) * 124); height: calc(var(--u) * 36); border-radius: calc(var(--u) * 18); background: #000; translate: -50% 0; }

/* Entrance: the large device settles, the phone rises in just after. */
.rnd-ipad, .rnd-desk, .rnd-phone { transition: opacity 0.7s ease, transform 0.9s cubic-bezier(0.22, 1, 0.36, 1); }
.rnd:not(.is-in) .rnd-ipad, .rnd:not(.is-in) .rnd-desk { opacity: 0; transform: translateY(18px) scale(0.985); }
.rnd:not(.is-in) .rnd-phone { opacity: 0; transform: translateY(42px); }
.rnd-phone { transition-delay: 0.15s; }

figcaption { margin-top: 1rem; color: #64748b; font-size: 0.85rem; text-align: center; }
.dark figcaption { color: #94a3b8; }

/* Phones: the iPhone alone, large. */
@media (max-width: 647px) {
  .rnd--tablet-phone .rnd-stage, .rnd--desktop-phone .rnd-stage { aspect-ratio: auto; max-width: 290px; margin-inline: auto; }
  .rnd-ipad, .rnd-desk { display: none; }
  .rnd-phone { position: relative; top: auto; right: auto; width: 100%; }
}
/* Before the observer runs (no JS, a crawler, a print) everything shows. */
@media (scripting: none) { .rnd:not(.is-in) .rnd-ipad, .rnd:not(.is-in) .rnd-desk, .rnd:not(.is-in) .rnd-phone { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .rnd-ipad, .rnd-desk, .rnd-phone { transition: none; }
  .rnd:not(.is-in) .rnd-ipad, .rnd:not(.is-in) .rnd-desk, .rnd:not(.is-in) .rnd-phone { opacity: 1; transform: none; }
}
</style>
